import React from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Button,
  Box,
  Typography,
  Alert,
  Snackbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link,
  Autocomplete,
  List,
  ListItem,
  Tooltip,
  ToggleButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SignalCellularConnectedNoInternet0BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import RestoreIcon from '@mui/icons-material/Restore';
import { formatTimestampAge } from '../utils/formatTimestampAge';
import { DataTable } from '../components/DataTable';
import { EmailPreviewModal } from '../components/edge-account-digests/EmailPreviewModal';
import { AttentionZones } from '../components/edge-account-digests/AttentionZones';
import { edgeAccountDigestsApi, locationsApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { extractApiError, extractApiErrorInfo } from '../utils/apiErrors';
import { API_CONFIG, ENV } from '../api/config';
import type { EdgeAccountDigest, DigestFilters } from '../api/config';

// Mirrors Location#parse_email_addresses on the backend.
// Stored as a comma-separated string column — parsing here is a workaround.
const parseEmails = (str: string): string[] =>
  Array.from(new Set(str.split(/[,:;]/).map(e => e.trim()).filter(Boolean)));

// The attention zones must stay fresh on a CSR's tab that's been left open and
// idle overnight, so we poll (refetchIntervalInBackground keeps it ticking even
// when the tab isn't focused). 60s is plenty — failures surface within a minute
// of the auto-send job recording them, and the /attention query is small.
const POLL_INTERVAL_MS = 60_000;

export const EdgeAccountDigestsPage: React.FC = () => {
  const [previewDigestId, setPreviewDigestId] = React.useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [selectedDigests, setSelectedDigests] = React.useState<number[]>([]);
  const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [cancelAutoSendDialogOpen, setCancelAutoSendDialogOpen] = React.useState(false);
  const [cancelAutoSendDigest, setCancelAutoSendDigest] = React.useState<EdgeAccountDigest | null>(null);
  const [restoreAutoSendDialogOpen, setRestoreAutoSendDialogOpen] = React.useState(false);
  const [restoreAutoSendDigest, setRestoreAutoSendDigest] = React.useState<EdgeAccountDigest | null>(null);
  const [sendNowDialogOpen, setSendNowDialogOpen] = React.useState(false);
  const [sendNowDigest, setSendNowDigest] = React.useState<EdgeAccountDigest | null>(null);
  // When approve/send is refused by an *overridable* gate lock (outage/silent),
  // surface a warning modal that lets the rep force it through (sc-6006). `kind`
  // routes the retry back to the right mutation with override: true.
  const [overridePrompt, setOverridePrompt] = React.useState<
    { kind: 'approve' | 'send' | 'approveSend'; id: number; message: string } | null
  >(null);
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' | 'warning' } | null>(null);
  // When bulk-approve has per-digest failures, surface the breakdown in a dialog
  // so the AM can see *why* each one failed instead of just which ones did.
  const [bulkApproveErrors, setBulkApproveErrors] = React.useState<
    Array<{ digest_id: number; location_name?: string; error: string }> | null
  >(null);
  
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [locationFilter, setLocationFilter] = React.useState<string>(searchParams.get('location_id') || '');
  const [sectionFilter, setSectionFilter] = React.useState<string>('');
  // Scope the table to digests generated in this morning's 4 AM run. Keyed off
  // created_at, not digest_date — the digest covers *yesterday* (digest_date =
  // Date.yesterday), so "today's batch" is a created_at match, and that also
  // holds across the weekend skip (Mon covers Fri) and historical reruns.
  const [createdToday, setCreatedToday] = React.useState<boolean>(false);

  const queryClient = useQueryClient();

  // Filters shared by both the pinned attention zones and the main table, so the
  // two always stay in step. The zones are a persistent triage view: they're
  // shown under every filter (not just the unfiltered default) and track the
  // same status/section/location/created-today criteria. The table always
  // de-dupes against them (exclude_attention) so a pinned digest shows in its
  // zone, never twice.
  const attentionFilters = React.useMemo<DigestFilters>(() => {
    const f: DigestFilters = {};
    if (statusFilter) f.status = statusFilter as DigestFilters['status'];
    if (locationFilter) f.location_id = Number(locationFilter);
    if (sectionFilter) f.section = sectionFilter;
    if (createdToday) f.created_today = true;
    return f;
  }, [statusFilter, locationFilter, sectionFilter, createdToday]);

  const filters = React.useMemo<DigestFilters>(
    () => ({ ...attentionFilters, exclude_attention: true }),
    [attentionFilters]
  );

  const { data: attention } = useQuery({
    queryKey: ['edge_account_digests', 'attention', attentionFilters],
    queryFn: () => edgeAccountDigestsApi.getAttention(attentionFilters),
    refetchInterval: POLL_INTERVAL_MS,
    // Keep ticking even when the tab is backgrounded — the whole point is to
    // surface overnight failures on a dormant tab without a manual refresh.
    refetchIntervalInBackground: true,
  });

  const {
    rows: digests,
    rowCount,
    isLoading,
    error,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['edge_account_digests', statusFilter, locationFilter, sectionFilter, createdToday],
    queryFn: (f, p) => edgeAccountDigestsApi.getPage(f, p),
    filters,
    defaultSort: { field: 'digest_date', sort: 'desc' },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.getAllWithoutFilters(),
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, override }: { id: number; override?: boolean }) =>
      edgeAccountDigestsApi.approve(id, override),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ message: 'Digest approved successfully', severity: 'success' });
      setApproveDialogOpen(false);
      setSelectedDigests([]);
    },
    onError: (error, variables) => {
      // Overridable lock (outage/silent) → offer the warning modal instead of a
      // dead-end error. Anything else stays a plain error (sc-6006).
      const info = extractApiErrorInfo(error, 'Failed to approve digest');
      if (info.overridable) {
        setApproveDialogOpen(false);
        setOverridePrompt({ kind: 'approve', id: variables.id, message: info.message });
      } else {
        setSnackbar({ message: info.message, severity: 'error' });
      }
    },
  });

  const bulkApproveMutation = useMutation({
    mutationFn: (ids: number[]) =>
      edgeAccountDigestsApi.bulkApprove(ids),
    // Clear any prior failures dialog before the new run so a stale dialog from
    // a previous bulk approve never lingers behind a successful follow-up.
    onMutate: () => {
      setBulkApproveErrors(null);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      if (data.errors?.length > 0) {
        setBulkApproveErrors(data.errors);
        setSnackbar({
          message: `Approved ${data.approved_count}. ${data.errors.length} failed — see details.`,
          severity: data.approved_count > 0 ? 'warning' : 'error'
        });
      } else {
        setSnackbar({ message: `Approved ${data.approved_count} digest(s)`, severity: 'success' });
      }
      setApproveDialogOpen(false);
      setSelectedDigests([]);
    },
    onError: (error) => {
      setBulkApproveErrors(null);
      setSnackbar({ message: extractApiError(error, 'Failed to approve digests'), severity: 'error' });
    },
  });

  const sendMutation = useMutation({
    mutationFn: ({ id, override }: { id: number; override?: boolean }) =>
      edgeAccountDigestsApi.send(id, override),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ message: 'Email sent', severity: 'success' });
    },
    onError: (error, variables) => {
      const info = extractApiErrorInfo(error, 'Failed to send email');
      if (info.overridable) {
        setOverridePrompt({ kind: 'send', id: variables.id, message: info.message });
      } else {
        setSnackbar({ message: info.message, severity: 'error' });
      }
    },
  });

  // "Approve & Send Now" for an auto-send-scheduled draft: run the same two
  // stable endpoints the manual flow uses — approve (lock + gate + HQ quote)
  // then send_email — immediately, instead of waiting for scheduled_send_at.
  // The parked EdgeAccountDigestAutoSendJob harmlessly skips when it later
  // fires (it sees the digest is no longer draft). If approve succeeds but
  // send fails, the digest is left 'approved' and recoverable via the manual
  // Send button — hence we always invalidate so the UI reflects the new state.
  const approveAndSendMutation = useMutation({
    mutationFn: async ({ id, override }: { id: number; override?: boolean }) => {
      await edgeAccountDigestsApi.approve(id, override);
      return edgeAccountDigestsApi.send(id, override);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ message: 'Digest approved and sent', severity: 'success' });
      setSendNowDialogOpen(false);
      setSendNowDigest(null);
    },
    onError: (error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      // Close the dialog rather than leaving its "Send Now" button live: if
      // approve already succeeded the digest is no longer a draft, so retrying
      // here would re-POST approve and 422. The invalidated list shows the new
      // state — recover via the row-level manual Send if approve went through.
      setSendNowDialogOpen(false);
      setSendNowDigest(null);
      // Offer the override retry (sc-6006). The overridable lock is almost
      // always refused at the approve step — the retry re-approves+sends with
      // override and succeeds. The one exception is an outage that opens in the
      // narrow window after approve succeeds but before send: the retry's
      // approve then 422s (already approved, not overridable) and dead-ends
      // here — recover via the row-level Send (which also offers override).
      const info = extractApiErrorInfo(error, 'Failed to approve and send digest. If it was approved, use the row Send button.');
      if (info.overridable) {
        setOverridePrompt({ kind: 'approveSend', id: variables.id, message: info.message });
      } else {
        setSnackbar({ message: info.message, severity: 'error' });
      }
    },
  });

  const sendApprovedMutation = useMutation({
    mutationFn: () => edgeAccountDigestsApi.sendApproved(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ 
        message: `Queued ${data.queued_count} approved digest(s) for sending`, 
        severity: 'success' 
      });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to queue emails'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => edgeAccountDigestsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ message: 'Digest archived', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedDigests([]);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to archive digest'), severity: 'error' });
    },
  });

  const cancelAutoSendMutation = useMutation({
    mutationFn: (id: number) => edgeAccountDigestsApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ message: 'Auto-send cancelled', severity: 'success' });
      setCancelAutoSendDialogOpen(false);
      setCancelAutoSendDigest(null);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to cancel auto-send'), severity: 'error' });
    },
  });

  const restoreAutoSendMutation = useMutation({
    mutationFn: (id: number) => edgeAccountDigestsApi.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ message: 'Auto-send restored', severity: 'success' });
      setRestoreAutoSendDialogOpen(false);
      setRestoreAutoSendDigest(null);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to restore auto-send'), severity: 'error' });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => edgeAccountDigestsApi.bulkDelete(ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ 
        message: `Archived ${data.deleted_count} digest(s)`,
        severity: 'success'
      });
      setDeleteDialogOpen(false);
      setSelectedDigests([]);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to archive digests'), severity: 'error' });
    },
  });

  const bulkMarkSentMutation = useMutation({
    mutationFn: (ids: number[]) => edgeAccountDigestsApi.bulkMarkSent(ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['edge_account_digests'] });
      setSnackbar({ 
        message: `Marked ${data.marked_count} digest(s) as sent`, 
        severity: 'success' 
      });
      setSelectedDigests([]);
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to mark digests as sent'), severity: 'error' });
    },
  });

  const handlePreview = (digest: EdgeAccountDigest) => {
    setPreviewDigestId(digest.id);
    setPreviewOpen(true);
  };

  const handleApprove = (digest: EdgeAccountDigest) => {
    setSelectedDigests([digest.id]);
    setApproveDialogOpen(true);
  };

  const handleBulkApprove = () => {
    if (selectedDigests.length === 0) {
      setSnackbar({ message: 'Please select digests to approve', severity: 'error' });
      return;
    }
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = () => {
    if (selectedDigests.length === 1) {
      approveMutation.mutate({ id: selectedDigests[0] });
    } else {
      bulkApproveMutation.mutate(selectedDigests);
    }
  };

  // Confirm the override warning modal: re-run the refused action with
  // override: true, routed by kind (sc-6006).
  const handleOverrideConfirm = () => {
    if (!overridePrompt) return;
    const { kind, id } = overridePrompt;
    setOverridePrompt(null);
    if (kind === 'approve') {
      approveMutation.mutate({ id, override: true });
    } else if (kind === 'send') {
      sendMutation.mutate({ id, override: true });
    } else {
      approveAndSendMutation.mutate({ id, override: true });
    }
  };

  const handleSendApproved = () => {
    if (window.confirm('Send all approved digests?')) {
      sendApprovedMutation.mutate();
    }
  };

  const handleDelete = (digest: EdgeAccountDigest) => {
    setSelectedDigests([digest.id]);
    setDeleteDialogOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedDigests.length === 0) {
      setSnackbar({ message: 'Please select digests to delete', severity: 'error' });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedDigests.length === 1) {
      deleteMutation.mutate(selectedDigests[0]);
    } else {
      bulkDeleteMutation.mutate(selectedDigests);
    }
  };

  const handleBulkMarkSent = () => {
    if (selectedDigests.length === 0) {
      setSnackbar({ message: 'Please select digests to mark as sent', severity: 'error' });
      return;
    }
    bulkMarkSentMutation.mutate(selectedDigests);
  };

  const columns: GridColDef[] = [
    {
      field: 'location',
      headerName: 'Location',
      width: 330,
      valueGetter: (params: GridRenderCellParams<EdgeAccountDigest>) =>
        params.row?.location?.name || 'Unknown',
      renderCell: (params: GridRenderCellParams<EdgeAccountDigest>) => {
        const loc = params.row?.location;
        if (!loc?.id) return params.row?.location?.name || 'Unknown';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Link
              component={RouterLink}
              to={`/locations/${loc.id}/dashboard`}
              sx={{ textDecoration: 'none' }}
            >
              {loc.name || `Location #${loc.id}`}
            </Link>
            {loc.uses_stock_truck && (
              <Tooltip
                title={
                  <>
                    <div><strong>Free Stock Truck</strong></div>
                    <div>Route: {loc.stock_truck_route || 'Not set'}</div>
                  </>
                }
                arrow
              >
                <LocalShippingIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'digest_date',
      headerName: 'Date',
      filterable: false,
      width: 120,
      valueGetter: (params: GridRenderCellParams<EdgeAccountDigest>) => {
        if (!params.row?.digest_date) return '';
        const [year, month, day] = params.row.digest_date.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString();
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      renderCell: (params: GridRenderCellParams<EdgeAccountDigest>) => {
        const status = params.row?.status || 'draft';
        const colorMap: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'info'> = {
          draft: 'default',
          approved: 'primary',
          pending_send: 'info',
          sent: 'success',
          cancelled: 'warning',
        };

        // A draft with a prior failed auto-quote attempt looks the same as a
        // fresh draft on the API. Surface the failure as a red chip + tooltip
        // so AMs see drafts needing attention vs ones that are simply awaiting
        // a first review.
        const failedAutoQuote =
          status === 'draft' && params.row?.quote_creation_status === 'failed';

        // A draft with a recorded auto-send failure (sc-5790) also looks like
        // a regular draft on the API. Surface the same way as the failed-
        // auto-quote chip so AMs see "needs manual attention" at a glance.
        const failedAutoSend =
          status === 'draft' && !!params.row?.auto_send_failure_reason;

        // A draft with `scheduled_send_at` set is queued for auto-send at that
        // time (no failure recorded). Surface as a clock-icon hint next to the
        // draft chip so AMs know the digest will move automatically unless
        // they cancel.
        const scheduledForAutoSend =
          status === 'draft' && !!params.row?.scheduled_send_at && !failedAutoSend;

        // Health indicator: only show next to draft rows (other statuses are
        // frozen — quantities locked at approval — so age of the underlying
        // BIS state isn't actionable). Outage outranks staleness because the
        // gate will hard-refuse approval on an open DeviceOutage; surfacing
        // both would be visually noisy and the outage message is the more
        // useful one for the rep.
        const healthIcon =
          status === 'draft'
            ? (() => {
                if (params.row?.has_active_outage) {
                  return (
                    <Tooltip
                      title="Approval blocked — covering device is in an active outage. Most clear within ~30 min; try again shortly."
                      arrow
                    >
                      <SignalCellularConnectedNoInternet0BarIcon
                        fontSize="small"
                        sx={{ color: 'error.main', ml: 0.5, verticalAlign: 'middle' }}
                        aria-label="covering device in active outage"
                      />
                    </Tooltip>
                  );
                }

                if (!params.row?.inventory_data_timestamp) return null;
                const { timeStr, agoStr, freshness } = formatTimestampAge(
                  params.row.inventory_data_timestamp!,
                );
                if (freshness === 'fresh') return null;
                const Icon = freshness === 'silent' ? ErrorOutlineIcon : WarningAmberIcon;
                const color = freshness === 'silent' ? 'error.main' : 'warning.main';
                const tooltip =
                  freshness === 'silent'
                    ? `Approval blocked — inventory data is silent (last scan ${timeStr}, ${agoStr}). Resumes once fresh sensor data arrives.`
                    : `Inventory data is stale — last scan ${timeStr}, ${agoStr}.`;
                return (
                  <Tooltip title={tooltip} arrow>
                    <Icon
                      fontSize="small"
                      sx={{ color, ml: 0.5, verticalAlign: 'middle' }}
                      aria-label={`inventory data ${freshness}`}
                    />
                  </Tooltip>
                );
              })()
            : null;

        if (failedAutoSend) {
          const attemptedAt = params.row?.auto_send_attempted_at
            ? new Date(params.row.auto_send_attempted_at).toLocaleString()
            : null;
          const reason = params.row?.auto_send_failure_reason;
          const detail = params.row?.auto_send_failure_message;
          const tooltip = [
            attemptedAt ? `Auto-send attempted ${attemptedAt}` : 'Auto-send attempted',
            reason ? `Reason: ${reason}` : null,
            detail || null,
            'Needs manual approval.',
          ]
            .filter(Boolean)
            .join(' — ');
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Tooltip title={tooltip} arrow>
                <Chip label="draft (auto-send failed)" color="error" size="small" />
              </Tooltip>
              {healthIcon}
            </Box>
          );
        }

        if (failedAutoQuote) {
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Tooltip
                title={params.row?.quote_creation_error || 'Auto-quote creation failed'}
                arrow
              >
                <Chip label="draft (failed)" color="error" size="small" />
              </Tooltip>
              {healthIcon}
            </Box>
          );
        }

        const scheduleIcon = scheduledForAutoSend
          ? (() => {
              const at = new Date(params.row!.scheduled_send_at!);
              const timeStr = at.toLocaleString();
              return (
                <Tooltip
                  title={`Scheduled to auto-send at ${timeStr}. Cancel from the preview to stop.`}
                  arrow
                >
                  <ScheduleSendIcon
                    fontSize="small"
                    sx={{ color: 'info.main', ml: 0.5, verticalAlign: 'middle' }}
                    aria-label="scheduled for auto-send"
                  />
                </Tooltip>
              );
            })()
          : null;

        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Chip label={status} color={colorMap[status] || 'default'} size="small" />
            {scheduleIcon}
            {healthIcon}
          </Box>
        );
      },
    },
    {
      field: 'sections_included',
      headerName: 'Sections',
      filterable: false,
      width: 200,
      valueGetter: (params: GridRenderCellParams<EdgeAccountDigest>) => 
        params.row?.sections_included?.join(', ') || 'None',
    },
    {
      field: 'location_dashboard',
      headerName: 'Inventory Dashboard',
      filterable: false,
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EdgeAccountDigest>) => {
        const slug = params.row?.location?.slug;
        if (!slug) return null;
        const dashboardUrl = ENV.isProduction
          ? `https://edge.symplirent.com/reports/${slug}`
          : `${API_CONFIG.baseURL}/reports/${slug}`;
        return (
          <Link
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: 'none' }}
          >
            View Dashboard
          </Link>
        );
      },
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      width: 280,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EdgeAccountDigest>) => {
        const emailStr = params.row?.location?.digest_recipient_email;
        if (!emailStr) {
          const fallback = params.row?.recipient_email;
          return fallback ? (
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{fallback}</Typography>
          ) : null;
        }
        const emails = parseEmails(emailStr);
        if (emails.length === 0) return null;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            {emails.map((email: string) => (
              <Typography key={email} variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {email}
              </Typography>
            ))}
          </Box>
        );
      },
    },
    {
      // Email-open engagement, surfaced by the index endpoint from HQ via the
      // HexaOms facade (same data already shown on the location dashboard's
      // Digests tab). Numeric field so the column sorts; not filterable, to
      // match the adjacent columns. Non-sent rows have no email yet, so they
      // render "—" naturally — no status gating needed.
      field: 'email_open_count',
      headerName: 'Opened',
      width: 110,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EdgeAccountDigest>) =>
        params.row?.email_opened ? (
          <Tooltip
            title={`Opened ${params.row.email_open_count} time${params.row.email_open_count === 1 ? '' : 's'}`}
            arrow
          >
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              ✓ {params.row.email_open_count}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            —
          </Typography>
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 400,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EdgeAccountDigest>) => {
        const digest = params.row;

        // Auto-send-scheduled drafts (no recorded failure) get only the
        // Preview + Cancel Auto-Send pair. Approve would race the send-time
        // job; Delete would destroy a customer-bound digest mid-window.
        // Once the send-time job records a failure, the digest flips back
        // to the manual-queue flavor and gets the usual buttons.
        const isAutoSendScheduled =
          digest?.status === 'draft'
          && !!digest?.scheduled_send_at
          && !digest?.auto_send_failure_reason;

        // A cancelled digest whose scheduled_send_at is still in the future
        // can be un-cancelled — the still-parked AutoSendJob will see `draft`
        // when it fires. Once scheduled_send_at is in the past, that job has
        // fired and bailed, so restore is no longer safe.
        const isRestorable =
          digest?.status === 'cancelled'
          && !!digest?.scheduled_send_at
          && new Date(digest.scheduled_send_at).getTime() > Date.now();

        if (isAutoSendScheduled) {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<PreviewIcon />}
                onClick={() => handlePreview(digest)}
              >
                Preview
              </Button>
              <Button
                size="small"
                startIcon={<SendIcon />}
                onClick={() => {
                  setSendNowDigest(digest);
                  setSendNowDialogOpen(true);
                }}
                variant="contained"
                color="primary"
              >
                Send Now
              </Button>
              <Button
                size="small"
                startIcon={<CancelScheduleSendIcon />}
                onClick={() => {
                  setCancelAutoSendDigest(digest);
                  setCancelAutoSendDialogOpen(true);
                }}
                variant="outlined"
                color="warning"
              >
                Cancel Send
              </Button>
            </Box>
          );
        }

        if (isRestorable) {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<PreviewIcon />}
                onClick={() => handlePreview(digest)}
              >
                Preview
              </Button>
              <Button
                size="small"
                startIcon={<RestoreIcon />}
                onClick={() => {
                  setRestoreAutoSendDigest(digest);
                  setRestoreAutoSendDialogOpen(true);
                }}
                variant="outlined"
                color="primary"
              >
                Restore Auto-Send
              </Button>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<PreviewIcon />}
              onClick={() => handlePreview(digest)}
            >
              Preview
            </Button>
            {digest?.status === 'draft' && (
              <Button
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleApprove(digest)}
                variant="outlined"
              >
                Approve
              </Button>
            )}
            {digest?.status === 'approved' && (
              <Button
                size="small"
                startIcon={<SendIcon />}
                onClick={() => sendMutation.mutate({ id: digest.id })}
                variant="contained"
                color="primary"
              >
                Send
              </Button>
            )}
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(digest)}
              variant="outlined"
              color="error"
            >
              Archive
            </Button>
          </Box>
        );
      },
    },
  ];

  if (error) {
    return <Alert severity="error">Error loading digests</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Edge Account Digests
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedDigests.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                onClick={handleBulkApprove}
                disabled={bulkApproveMutation.isPending}
              >
                {bulkApproveMutation.isPending
                  ? `Approving ${selectedDigests.length}...`
                  : `Approve Selected (${selectedDigests.length})`}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleBulkMarkSent}
              >
                Mark Selected as Sent ({selectedDigests.length})
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Archive Selected ({selectedDigests.length})
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendApproved}
          >
            Send All Approved
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Section</InputLabel>
          <Select
            value={sectionFilter}
            onChange={(e: SelectChangeEvent) => setSectionFilter(e.target.value)}
            label="Filter by Section"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="restock">Restock</MenuItem>
            <MenuItem value="restock_all_stocked">All Stocked Up</MenuItem>
            <MenuItem value="daily_usage">Daily Usage</MenuItem>
            <MenuItem value="oos">Out of Stock</MenuItem>
            <MenuItem value="rma">RMA</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          options={locations}
          value={locations.find(l => l.id.toString() === locationFilter) ?? null}
          onChange={(_e, location) => setLocationFilter(location?.id?.toString() ?? '')}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Location" />
          )}
          sx={{ minWidth: 250 }}
        />
        <Tooltip
          title="Show only digests generated in this morning's run. These cover yesterday's activity (digest date is yesterday), but were created today."
          arrow
        >
          <ToggleButton
            value="created_today"
            selected={createdToday}
            onChange={() => setCreatedToday((v) => !v)}
            color="primary"
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Created Today
          </ToggleButton>
        </Tooltip>
      </Box>

      <AttentionZones
        data={attention}
        onPreview={handlePreview}
        onApprove={handleApprove}
        onArchive={handleDelete}
        onSendNow={(digest) => { setSendNowDigest(digest); setSendNowDialogOpen(true); }}
        onCancel={(digest) => { setCancelAutoSendDigest(digest); setCancelAutoSendDialogOpen(true); }}
        onSend={(digest) => sendMutation.mutate({ id: digest.id })}
        onRestore={(digest) => { setRestoreAutoSendDigest(digest); setRestoreAutoSendDialogOpen(true); }}
      />


      <DataTable
        rows={digests}
        columns={columns}
        loading={isLoading}
        getRowId={(row: EdgeAccountDigest) => row.id.toString()}
        checkboxSelection={true}
        onSelectionModelChange={(selection: GridRowSelectionModel) => {
          setSelectedDigests(selection.map((id) => typeof id === 'string' ? Number(id) : id as number));
        }}
        selectionModel={selectedDigests.map(id => id.toString()) as GridRowSelectionModel}
        fullHeight
        getRowHeight={() => 'auto'}
        dataGridSx={{ '& .MuiDataGrid-cell': { py: 1, minHeight: 52 } }}
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        pageSizeOptions={[25, 50, 100]}
      />

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewDigestId(null); }}
        digestId={previewDigestId}
      />

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve Digest{selectedDigests.length > 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedDigests.length === 1
              ? 'Approve this digest and create the HexaHQ quote? Quantities will be locked.'
              : `Approve ${selectedDigests.length} digests and create HexaHQ quotes? Quantities will be locked.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApproveConfirm}
            variant="contained"
            disabled={approveMutation.isPending || bulkApproveMutation.isPending}
          >
            {(approveMutation.isPending || bulkApproveMutation.isPending) ? 'Creating quote...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override warning modal (sc-6006): shown when approve/send was refused
          by an overridable lock (outage/silent). Force-sends possibly-stale
          data to the customer, so the consequence is spelled out. */}
      <Dialog open={!!overridePrompt} onClose={() => setOverridePrompt(null)}>
        <DialogTitle>
          {overridePrompt?.kind === 'approve' ? 'Override approval lock?' : 'Override send lock?'}
        </DialogTitle>
        <DialogContent>
          {overridePrompt?.message && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {overridePrompt.message}
            </Alert>
          )}
          <Typography>
            This bypasses a safety check and will send possibly-stale inventory
            data to the customer.{' '}
            {overridePrompt?.kind === 'approve' ? 'Approve anyway?' : 'Send anyway?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverridePrompt(null)}>Cancel</Button>
          <Button
            onClick={handleOverrideConfirm}
            variant="contained"
            color="warning"
            disabled={approveMutation.isPending || sendMutation.isPending || approveAndSendMutation.isPending}
          >
            {overridePrompt?.kind === 'approve' ? 'Approve Anyway' : 'Send Anyway'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Archive Digest{selectedDigests.length > 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <Typography>
            Archive {selectedDigests.length > 1
              ? `these ${selectedDigests.length} digests`
              : 'this digest'}? {selectedDigests.length > 1 ? 'They' : 'It'} will be
            hidden from the active list but retained for record-keeping (viewable
            via &ldquo;Show archived&rdquo;). This can&rsquo;t be undone from here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelAutoSendDialogOpen} onClose={() => setCancelAutoSendDialogOpen(false)}>
        <DialogTitle>Cancel Auto-Send</DialogTitle>
        <DialogContent>
          <Typography>
            Cancel the scheduled auto-send for this digest? The digest will be
            marked cancelled and will not be sent to the customer.
          </Typography>
          {cancelAutoSendDigest?.scheduled_send_at && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Was scheduled for {new Date(cancelAutoSendDigest.scheduled_send_at).toLocaleString()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelAutoSendDialogOpen(false)}>Keep Scheduled</Button>
          <Button
            onClick={() => cancelAutoSendDigest && cancelAutoSendMutation.mutate(cancelAutoSendDigest.id)}
            variant="contained"
            color="warning"
            disabled={cancelAutoSendMutation.isPending}
          >
            {cancelAutoSendMutation.isPending ? 'Cancelling...' : 'Cancel Auto-Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendNowDialogOpen} onClose={() => setSendNowDialogOpen(false)}>
        <DialogTitle>Approve &amp; Send Now</DialogTitle>
        <DialogContent>
          <Typography>
            Approve this digest and email it to the customer right now? This
            creates the HexaHQ quote and sends the email immediately, bypassing
            the scheduled auto-send time.
          </Typography>
          {sendNowDigest?.scheduled_send_at && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Was scheduled for {new Date(sendNowDigest.scheduled_send_at).toLocaleString()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendNowDialogOpen(false)}>Keep Scheduled</Button>
          <Button
            onClick={() => sendNowDigest && approveAndSendMutation.mutate({ id: sendNowDigest.id })}
            variant="contained"
            color="primary"
            disabled={approveAndSendMutation.isPending}
          >
            {approveAndSendMutation.isPending ? 'Sending...' : 'Send Now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreAutoSendDialogOpen} onClose={() => setRestoreAutoSendDialogOpen(false)}>
        <DialogTitle>Restore Auto-Send</DialogTitle>
        <DialogContent>
          <Typography>
            Restore this digest to the auto-send queue? The customer will receive
            the digest at the originally-scheduled time. The cancel record will
            be cleared.
          </Typography>
          {restoreAutoSendDigest?.scheduled_send_at && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Will send at {new Date(restoreAutoSendDigest.scheduled_send_at).toLocaleString()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreAutoSendDialogOpen(false)}>Keep Cancelled</Button>
          <Button
            onClick={() => restoreAutoSendDigest && restoreAutoSendMutation.mutate(restoreAutoSendDigest.id)}
            variant="contained"
            color="primary"
            disabled={restoreAutoSendMutation.isPending}
          >
            {restoreAutoSendMutation.isPending ? 'Restoring...' : 'Restore Auto-Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!bulkApproveErrors}
        onClose={() => setBulkApproveErrors(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk approve — failures</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The following digests could not be approved:
          </Typography>
          <List dense disablePadding>
            {bulkApproveErrors?.map((e) => (
              <ListItem
                key={e.digest_id}
                sx={{ display: 'block', borderBottom: '1px solid', borderColor: 'divider', py: 1 }}
              >
                <Typography variant="subtitle2">
                  {e.location_name || `Digest ${e.digest_id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {e.error}
                </Typography>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkApproveErrors(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity}
          onClose={() => setSnackbar(null)}
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

